/*
 * Participant Config client — per DESIGN-PARTICIPANT-CONFIG-V1 §B.5.
 *
 * Reads the resolved `data_collection` section for an event from settings'
 * participant-config endpoint:
 *   GET /api/events/:eventId/participant-config/resolved?section=data_collection
 *
 * settings' backend chassis may not be live yet (see docs/current/
 * MY-API-CHASSIS-DESIGN.md — design-only as of this build), so this seam is
 * built typed-first against the documented contract, with an honest
 * 'unconfigured' base URL when unset — mirroring the existing
 * VITE_TICKETS_API_BASE / ticketsApiBase() pattern already shipped for
 * orders (api.ts). Never mock served to a signed-in user; a fixture for
 * local dev/tests lives separately in participantConfig.fixture.ts and is
 * never imported from this file or from DataProvider.tsx.
 */

/** Field keys this app can collect on the participant profile, per B.5.
 *  `tshirt_size` + `language` (CMT-2026-08-29-022 / BOARDROOM-2026-08-29-110613):
 *  the wizard's T-shirt-size and language questions are EVENT-LEVEL toggles.
 *  Key names CONFIRMED by settings (SETTINGS-2026-08-29-113258 via
 *  BOARDROOM-2026-08-29-115054) and ground-truthed on the live stage endpoint
 *  2026-08-29: both keys arrive on the resolved read at default-tier
 *  `collect:false` — OFF-BY-CONSTRUCTION everywhere until an event owner opts
 *  in. Semantics: `collect:false` ⇒ the question NEVER renders; an ABSENT key
 *  ⇒ ask (fail-toward-asking, the established questionnaire-gate posture). */
export type ParticipantFieldKey =
  | 'given_name'
  | 'family_name'
  | 'dob'
  | 'gender'
  | 'nationality'
  | 'phone'
  | 'emergency_contact'
  | 'tshirt_size'
  | 'language'

const KNOWN_FIELD_KEYS: ReadonlySet<string> = new Set<ParticipantFieldKey>([
  'given_name',
  'family_name',
  'dob',
  'gender',
  'nationality',
  'phone',
  'emergency_contact',
  'tshirt_size',
  'language',
])

export interface DataCollectionFieldConfig {
  key: ParticipantFieldKey
  collect: boolean
}

export interface DataCollectionResolved {
  section: 'data_collection'
  eventId: string
  fields: DataCollectionFieldConfig[]
}

/** LIVE wire shape of the resolved endpoint — GROUND-TRUTHED against stage
 *  2026-08-29 (authed probe on e5635953): sections are TOP-LEVEL keys and each
 *  section is a KEYED OBJECT of per-field entries carrying `value` +
 *  `source_tier`, e.g.
 *    { data_collection: { gender: { value: { collect: true, required: true },
 *      source_tier: 'event' }, ... }, schema_version: 1 }
 *  This replaces the earlier fixture-era `{ section, fields: [{key, collect}] }`
 *  assumption, which never matched the live service (the mapper produced an
 *  empty field list ⇒ every toggle silently fell back to ask). `required` and
 *  `source_tier` are present on the wire but deliberately unconsumed here —
 *  this seam only answers "does the event collect this field?". */
interface ResolvedParticipantConfigResponse {
  data_collection?: Record<
    string,
    { value?: { collect?: unknown; required?: unknown } | null; source_tier?: string } | null
  > | null
  schema_version?: number
}

export function participantConfigApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_SETTINGS_API_BASE || null
}

/** Map the wire shape into this app's contract. Pure — unit-tested. Unknown
 *  field keys (future config additions this build doesn't know about yet)
 *  are dropped rather than crashing the readiness computation. Entries whose
 *  `value.collect` is not LITERALLY boolean are also DROPPED (⇒ absent ⇒ the
 *  question is asked): a malformed entry (e.g. a null value object or a
 *  string 'false') must fail TOWARD asking — a `Boolean()` coercion would
 *  silently read some of those as OFF, the wrong posture (CMT-2026-08-29-022). */
export function mapResolvedDataCollection(
  eventId: string,
  json: ResolvedParticipantConfigResponse,
): DataCollectionResolved {
  const fields: DataCollectionFieldConfig[] = Object.entries(json.data_collection ?? {})
    .filter(
      (entry): entry is [ParticipantFieldKey, { value: { collect: boolean } }] =>
        KNOWN_FIELD_KEYS.has(entry[0]) && typeof entry[1]?.value?.collect === 'boolean',
    )
    .map(([key, cfg]) => ({ key, collect: cfg.value.collect }))
  return { section: 'data_collection', eventId, fields }
}

export async function fetchParticipantConfigDataCollection(
  eventId: string,
  base: string,
  idToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DataCollectionResolved> {
  const url =
    `${base.replace(/\/$/, '')}/api/events/${encodeURIComponent(eventId)}` +
    `/participant-config/resolved?section=data_collection`
  const res = await fetchImpl(url, { headers: { Authorization: `Bearer ${idToken}` } })
  if (!res.ok) throw new Error(`participant-config resolved fetch failed (${res.status})`)
  return mapResolvedDataCollection(eventId, (await res.json()) as ResolvedParticipantConfigResponse)
}

export function collectsField(config: DataCollectionResolved, key: ParticipantFieldKey): boolean {
  return config.fields.some((f) => f.key === key && f.collect)
}
